import { Modal } from 'components/molecules/Modal'
import type { SignupModalProps } from './types'
import { closeModal, openModal, cn, getError } from 'lib/utils'
import { EModalType } from './enums'
import {
  Button,
  EButtonVariant,
  EButtonStyleVariant,
} from 'components/atoms/Button'
import { Input } from 'components/atoms/Input'
import { PasswordInput } from 'components/atoms/PasswordInput'
import { Textarea } from 'components/atoms/Textarea'
import { Checkbox } from 'components/atoms/Checkbox'
import { FormError } from 'components/atoms/FormError'
import { useForms } from 'lib/contexts/FormsContext'
import { useSignUpForm } from './hooks'
import type { RegisterField } from 'lib/types/api'

const mainClass = 'form'

import type { FormErrors } from 'lib/types/forms'

const renderField = (field: RegisterField, errors: FormErrors) => {
  const placeholder = `${field.label}${field.required ? ' *' : ''}`
  const fieldError = getError(errors, field.name)

  if (field.type === 'boolean') {
    return (
      <Checkbox key={field.name} name={field.name} value="1" error={fieldError}>
        {placeholder}
      </Checkbox>
    )
  }

  if (field.type === 'text') {
    return (
      <Textarea
        key={field.name}
        placeholder={placeholder}
        name={field.name}
        error={fieldError}
      />
    )
  }

  return (
    <Input
      key={field.name}
      type="text"
      placeholder={placeholder}
      name={field.name}
      error={fieldError}
    />
  )
}

export const SignupModal = ({
  title,
  submitButton,
  termsLink,
}: SignupModalProps) => {
  const { registerFields } = useForms()
  const { formRef, isSubmitting, errors, handleSubmit } = useSignUpForm()

  const handleTermsClick = (e: React.MouseEvent) => {
    e.preventDefault()
    openModal(EModalType.TERMS)
  }

  return (
    <Modal title={title} onClose={closeModal}>
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className={cn(mainClass, `${mainClass}--register`)}
      >
        <FormError message={getError(errors, 'error')} />
        <Input
          placeholder="Email *"
          type="email"
          name="email"
          error={getError(errors, 'email')}
        />
        <PasswordInput
          placeholder="Password *"
          name="password"
          error={getError(errors, 'password')}
        />
        <PasswordInput
          placeholder="Password confirmation *"
          name="password_confirmation"
          error={getError(errors, 'password_confirmation')}
        />
        <Input
          placeholder="Company name *"
          type="text"
          name="company_name"
          error={getError(errors, 'company_name')}
        />

        {registerFields.length > 0 && (
          <div id="register_fields">
            {registerFields.map((field) => renderField(field, errors))}
          </div>
        )}

        <Checkbox
          name="terms_accepted"
          value="1"
          error={getError(errors, 'terms_accepted')}
        >
          I accept the{' '}
          <a className="link" href="#" onClick={handleTermsClick}>
            {termsLink}
          </a>
        </Checkbox>

        <Button
          type="submit"
          variant={EButtonVariant.BUTTON}
          buttonVariant={EButtonStyleVariant.PRIMARY}
          disabled={isSubmitting}
        >
          {submitButton}
        </Button>
      </form>
    </Modal>
  )
}
